import { useEffect, useState } from 'react'
import { creatorPostsAPI } from '../services/api'
import type { CreatorPost, PostsSummary as Summary } from '../types'

const fmt = (n: number) => n.toLocaleString()

interface PostsSummaryProps {
  /** The creator's posted links: the summary is read again when they change. */
  posts: CreatorPost[]
}

/**
 * How the creator's own posts are doing, from counts the network gave or the
 * creator typed in (a post with no counts is listed, never averaged as 0).
 * Per network, since a view on X (an impression) isn't a view on YouTube.
 */
const PostsSummary = ({ posts }: PostsSummaryProps) => {
  const [summary, setSummary] = useState<Summary | null>(null)

  useEffect(() => {
    if (posts.length === 0) {
      setSummary(null)
      return
    }
    let stopped = false
    // Optional: nothing shows when it can't be loaded.
    Promise.resolve()
      .then(() => creatorPostsAPI.summary())
      .then((s) => !stopped && setSummary(s))
      .catch(() => undefined)
    return () => {
      stopped = true
    }
  }, [posts])

  if (!summary || summary.posts === 0) return null

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="How your posts are doing">
      <h2 className="text-sm font-semibold text-gray-900">How your posts are doing</h2>
      <ul className="mt-2 space-y-3">
        {summary.networks.map((n) => (
          <li key={n.network} className="text-sm text-gray-700" data-testid={`summary-${n.network}`}>
            <p>
              <span className="font-medium text-gray-900">{n.network_name}</span> · {n.posts}{' '}
              {n.posts === 1 ? 'post' : 'posts'}
              {n.measured < n.posts && `, ${n.measured} with counts`}
              {n.avg_views !== null && ` · ${fmt(n.avg_views)} views on average`}
              {n.avg_likes !== null && `, ${fmt(n.avg_likes)} likes`}
              {n.best && n.measured > 1 && (
                <>
                  {' · '}
                  <a href={n.best.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    Best: {fmt(n.best.views)} views
                  </a>
                </>
              )}
            </p>
            {n.kinds.length > 1 && (
              <p className="text-xs text-gray-500">
                {n.kinds.map((k) => `${k.label}: ${fmt(k.avg_views ?? 0)} views on average (${k.measured})`).join(' · ')}
              </p>
            )}
            {n.measured > 0 && !n.enough_for_pattern && (
              <p className="text-xs text-gray-500">
                Only {n.measured} {n.measured === 1 ? 'post has' : 'posts have'} counts: too few to call a pattern yet (
                {summary.min_for_pattern} or more).
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

export default PostsSummary
